import { ApolloClient, gql, useQuery } from '@apollo/client'

/**
 * Runtime probe: does the CONNECTED backend's GraphQL schema serve a given
 * field on a given type?
 *
 * FE and BE deploy independently (and the local dev stacks race the same
 * way), so a freshly deployed FE can face a backend that does not yet serve
 * a field the FE was built against. Any operation whose selection set (or
 * input) names such a field fails schema validation server-side and takes
 * the WHOLE operation down — e.g. a form's read query erroring with
 * "Cannot query field X on type Y". This hook lets callers keep new fields
 * out of their always-on documents and only query/send/render them once the
 * live schema confirms support (round2 R2-S3 precedent: `pricingMode` on
 * the contact/company forms).
 *
 * Introspection meta-fields (`__type`) are part of every GraphQL schema, so
 * the probe itself validates against ANY backend version. One request per
 * type name per session (Apollo cache-first), shared across callers.
 *
 * @returns `true`/`false` once the probe resolved; `undefined` while it is
 * in flight or if it failed — treat as "not supported" for anything
 * user-visible (safe default: pre-existing UI, field omitted from inputs).
 */
export const SCHEMA_TYPE_FIELDS = gql`
  query SchemaTypeFields($typeName: String!) {
    __type(name: $typeName) {
      name
      fields {
        name
      }
    }
  }
`

type TSchemaTypeFields = {
  __type: {
    name: string
    fields: Array<{ name: string }> | null
  } | null
}

/**
 * `useSchemaFieldSupport` with the probe's IN-FLIGHT state beside its answer
 * (HSY-235, COMPASS-1687).
 *
 * A surface that hydrates ONCE from whichever document answers first — the
 * contractor-price wizard's edit mode, which seeds an antd form from its read
 * and then guards against re-seeding — must not observe its data until the
 * probe that selects the document has settled, or a legacy read that wins the
 * race locks the cent-rounded value in and the twin's Decimal is never seen.
 * `loading` is that signal: `true` only while the introspection request is
 * outstanding with NO answer yet (a cached answer under cache-and-network is
 * an answer, so a background revalidation never re-arms it); `false` once it
 * resolved either way, failed, or was skipped — so a probe that errors still
 * releases the caller onto the legacy document rather than parking it.
 */
export type TSchemaFieldProbe = {
  /** `useSchemaFieldSupport`'s answer: `true`/`false` once resolved, else `undefined`. */
  supported: boolean | undefined
  /** The probe is outstanding and has not answered yet. */
  loading: boolean
}

/**
 * ALL-OF form (COMPASS-1190 Map Planning): a second document that selects
 * SEVERAL new fields on one type may only be sent once that type declares
 * EVERY one of them. `@include(if:)` is a runtime directive — it exempts
 * nothing from schema validation — so one unknown name fails the whole
 * operation whatever the flag's value, exactly as one undeclared argument
 * does. `supported` is `true` only when every name in `fieldNames` is served
 * (an unknown type serves none); `undefined` while in flight, skipped or
 * failed, with `loading` as described above.
 */
export const useSchemaFieldsProbe = (
  typeName: string,
  fieldNames: readonly string[],
  skip = false
): TSchemaFieldProbe => {
  const { data, loading } = useQuery<TSchemaTypeFields>(SCHEMA_TYPE_FIELDS, {
    variables: { typeName },
    skip,
  })
  if (skip || !data) return { supported: undefined, loading: !skip && loading }
  const served = new Set(
    // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
    (data.__type?.fields ?? []).map((field) => field.name)
  )
  return {
    supported: fieldNames.every((name) => served.has(name)),
    loading: false,
  }
}

/** The one-field form of `useSchemaFieldsProbe`. */
export const useSchemaFieldProbe = (
  typeName: string,
  fieldName: string,
  skip = false
): TSchemaFieldProbe => useSchemaFieldsProbe(typeName, [fieldName], skip)

/** Does `typeName` serve EVERY one of `fieldNames`? The probe's answer alone. */
export const useSchemaFieldsSupport = (
  typeName: string,
  fieldNames: readonly string[],
  skip = false
): boolean | undefined =>
  useSchemaFieldsProbe(typeName, fieldNames, skip).supported

export const useSchemaFieldSupport = (
  typeName: string,
  fieldName: string,
  // Callers whose feature is walled off for the session (e.g. contractor
  // sessions, PRODUCT-149) skip the probe request entirely — `undefined`
  // then reads as "not supported yet" and every dependent query stays
  // skipped too. Mirrors `useScheduledChangesHistorySupport(skip)`.
  skip = false
): boolean | undefined =>
  useSchemaFieldProbe(typeName, fieldName, skip).supported

/**
 * Combine several probes into one answer: `false` as soon as any one says
 * no (the combined answer can never become yes), `undefined` while any is
 * still open, `true` once every one has said yes. For a surface that must
 * only be offered when EVERY consumer of what it sends would honour it.
 */
export const allSupported = (
  answers: ReadonlyArray<boolean | undefined>
): boolean | undefined => {
  if (answers.some((answer) => answer === false)) return false
  if (answers.some((answer) => answer === undefined)) return undefined
  return true
}

/**
 * Imperative sibling of `useSchemaFieldSupport` for code that runs outside a
 * React hook (an `asyncModal` submit handler, a plain callback). Same probe,
 * same cache (one request per type name per session); resolves `false` on
 * any failure — treat as "not supported" and send the legacy document.
 */
export const schemaFieldSupported = async (
  client: ApolloClient<object>,
  typeName: string,
  fieldName: string
): Promise<boolean> => {
  try {
    const { data } = await client.query<TSchemaTypeFields>({
      query: SCHEMA_TYPE_FIELDS,
      variables: { typeName },
      fetchPolicy: 'cache-first',
    })
    return Boolean(
      // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
      data.__type?.fields?.some((field) => field.name === fieldName)
    )
  } catch {
    return false
  }
}

/**
 * INPUT-type sibling of `useSchemaFieldSupport` (REQ 3.12.5).
 *
 * Introspection exposes an input object type's fields under `inputFields`,
 * NOT `fields` — `__type(name: "UpdateTicketInput") { fields }` is always
 * null, so the output-type hook above can never see input types. What
 * hard-fails a MUTATION against a pre-deploy backend is the missing INPUT
 * field (e.g. `UpdateTicketInput.expectedVersion`: an unknown input field
 * fails GraphQL validation and takes the whole mutation down), so gates on
 * sending new input fields must probe with THIS hook, not the output one.
 *
 * Same semantics and plumbing: one cache-first probe per input type name per
 * session, shared across callers; `undefined` while in flight or on failure
 * — treat as "not supported" (omit the field from the input).
 */
/**
 * Whether a field of `typeName` (usually `Query`) accepts the argument
 * `argName`. The sibling of `schemaFieldSupported` for connection FILTER
 * arguments (graphene-django exposes `FilterSet` filters as plain arguments,
 * not as an input type), so a page can probe `allVismaExportLogs(propertyId:)`
 * before sending it to a backend that may not serve it yet. Unknown on any
 * failure, like the other probes.
 */
export const SCHEMA_FIELD_ARGS = gql`
  query SchemaFieldArgs($typeName: String!) {
    __type(name: $typeName) {
      name
      fields {
        name
        args {
          name
        }
      }
    }
  }
`

type TSchemaFieldArgs = {
  __type: {
    name: string
    fields: Array<{ name: string; args: Array<{ name: string }> }> | null
  } | null
}

export const schemaFieldArgSupported = async (
  client: ApolloClient<object>,
  typeName: string,
  fieldName: string,
  argName: string
): Promise<boolean> => {
  try {
    const { data } = await client.query<TSchemaFieldArgs>({
      query: SCHEMA_FIELD_ARGS,
      variables: { typeName },
      fetchPolicy: 'cache-first',
    })
    // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
    const field = data.__type?.fields?.find((f) => f.name === fieldName)
    return Boolean(field?.args?.some((arg) => arg.name === argName))
  } catch {
    return false
  }
}

export const SCHEMA_TYPE_INPUT_FIELDS = gql`
  query SchemaTypeInputFields($typeName: String!) {
    __type(name: $typeName) {
      name
      inputFields {
        name
      }
    }
  }
`

type TSchemaTypeInputFields = {
  __type: {
    name: string
    inputFields: Array<{ name: string }> | null
  } | null
}

/**
 * Imperative sibling of `useSchemaInputFieldSupport` for code that runs
 * outside a React hook (an `asyncModal` submit handler, a plain callback).
 * Same probe, same cache; resolves `false` on any failure — treat as "not
 * supported" and omit the field from the input.
 */
export const schemaInputFieldSupported = async (
  client: ApolloClient<object>,
  inputTypeName: string,
  fieldName: string
): Promise<boolean> => {
  try {
    const { data } = await client.query<TSchemaTypeInputFields>({
      query: SCHEMA_TYPE_INPUT_FIELDS,
      variables: { typeName: inputTypeName },
      fetchPolicy: 'cache-first',
    })
    return Boolean(
      // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
      data.__type?.inputFields?.some((field) => field.name === fieldName)
    )
  } catch {
    return false
  }
}

/**
 * ALL-OF form for INPUT types, the input-type twin of `useSchemaFieldsProbe`
 * (PRO-954): a save that sends SEVERAL new keys of one input type may only
 * send them once that input type declares EVERY one of them — one unknown
 * input field fails the whole mutation. `supported` is `true` only when every
 * name in `fieldNames` is served (an unknown input type serves none);
 * `undefined` while in flight, skipped or failed, with `loading` as described
 * on `useSchemaInputFieldProbe` below. Same request and cache as the one-field
 * form, so mixing the two on one input type costs nothing extra.
 */
export const useSchemaInputFieldsProbe = (
  inputTypeName: string,
  fieldNames: readonly string[],
  skip = false
): TSchemaFieldProbe => {
  const { data, loading } = useQuery<TSchemaTypeInputFields>(
    SCHEMA_TYPE_INPUT_FIELDS,
    {
      variables: { typeName: inputTypeName },
      skip,
    }
  )
  if (skip || !data) return { supported: undefined, loading: !skip && loading }
  const served = new Set(
    // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
    (data.__type?.inputFields ?? []).map((field) => field.name)
  )
  return {
    supported: fieldNames.every((name) => served.has(name)),
    loading: false,
  }
}

/**
 * `useSchemaInputFieldSupport` with the probe's IN-FLIGHT state beside its
 * answer — the input-type twin of `useSchemaFieldProbe` (PRODUCT-446). A
 * form whose save OMITS a gated input key while the probe is still open
 * (and whose backend defaults that omission — a ModelForm BooleanField
 * missing from the data cleans to False) needs to hold the save until the
 * probe has answered; `loading` is that signal, with the same semantics as
 * `TSchemaFieldProbe`: `true` only while the request is outstanding with no
 * answer yet, `false` once it resolved, failed or was skipped. The one-field
 * form of `useSchemaInputFieldsProbe`.
 */
export const useSchemaInputFieldProbe = (
  inputTypeName: string,
  fieldName: string,
  skip = false
): TSchemaFieldProbe =>
  useSchemaInputFieldsProbe(inputTypeName, [fieldName], skip)

export const useSchemaInputFieldSupport = (
  inputTypeName: string,
  fieldName: string,
  // Same skip escape hatch as `useSchemaFieldSupport`: a caller that only
  // needs the answer once a deferred surface opens (e.g. an in-place edit
  // form) passes `skip` until then so a read-only mount issues no probe.
  skip = false
): boolean | undefined =>
  useSchemaInputFieldProbe(inputTypeName, fieldName, skip).supported

/**
 * UNION-membership sibling of `useSchemaFieldSupport` (PRO-563).
 *
 * A `... on Member` inline fragment on a union whose schema does NOT (yet) list
 * `Member` in its `possibleTypes` fails GraphQL validation SERVER-SIDE and
 * takes the whole operation down — same deploy-window hazard as an unknown
 * field/input field, but for abstract types. Introspection exposes a union's
 * members under `possibleTypes`, so this probes whether `memberName` is a
 * possible type of `unionName` and lets callers select between a with-fragment
 * and a without-fragment query document accordingly. Same plumbing: one
 * cache-first `__type` probe per union name per session; `undefined` while in
 * flight or on failure — treat as "not a member" (omit the fragment).
 */
export const SCHEMA_UNION_POSSIBLE_TYPES = gql`
  query SchemaUnionPossibleTypes($typeName: String!) {
    __type(name: $typeName) {
      name
      possibleTypes {
        name
      }
    }
  }
`

type TSchemaUnionPossibleTypes = {
  __type: {
    name: string
    possibleTypes: Array<{ name: string }> | null
  } | null
}

/**
 * UNION-MEMBER probe with its in-flight state beside the answer, the
 * `useSchemaFieldProbe` shape: `supported` is `true`/`false` once the union
 * has been read, `undefined` while in flight, skipped or failed; `loading`
 * is true only while the request is outstanding. A caller that hands its
 * result to something irreversible (a PDF capture that starts on the first
 * completed query, COMPASS-1773's Browserless history) waits on `loading`,
 * never on `supported === undefined` — a failed probe is also `undefined`
 * and would wait forever.
 */
export const useSchemaUnionMemberProbe = (
  unionName: string,
  memberName: string,
  skip = false
): TSchemaFieldProbe => {
  const { data, loading } = useQuery<TSchemaUnionPossibleTypes>(
    SCHEMA_UNION_POSSIBLE_TYPES,
    { variables: { typeName: unionName }, skip }
  )
  if (skip || !data) return { supported: undefined, loading: !skip && loading }
  return {
    supported: Boolean(
      // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
      data.__type?.possibleTypes?.some((member) => member.name === memberName)
    ),
    loading: false,
  }
}

export const useSchemaUnionMemberSupport = (
  unionName: string,
  memberName: string,
  skip = false
): boolean | undefined =>
  useSchemaUnionMemberProbe(unionName, memberName, skip).supported

/**
 * ENUM-VALUE sibling of `useSchemaFieldSupport` (Data Exports PDF file type).
 *
 * A document that SENDS an enum value the connected backend's enum does not
 * (yet) declare — `createOrUpdateExportTemplate(exportFormat: PDF)` against a
 * backend without the PDF file type — fails GraphQL validation server-side
 * ("Value 'PDF' does not exist in 'ExportFormats' enum") and takes the whole
 * mutation down; a scheduled template saved in that window would fail every
 * run. A `schema.local-extensions.graphql` entry only keeps the BUILD green
 * across that deploy window — it says nothing about the backend the app is
 * talking to. Introspection lists an enum's members under `enumValues`, so
 * this probes whether `valueName` is one of them and lets a form withhold the
 * option until the live schema serves it.
 *
 * Same plumbing as the probes above, pinned `cache-first` (one request per
 * enum name per session, shared by every caller); `undefined` while in flight,
 * skipped or failed — treat as "not supported" (withhold the option).
 */
export const SCHEMA_ENUM_VALUES = gql`
  query SchemaEnumValues($typeName: String!) {
    __type(name: $typeName) {
      name
      enumValues {
        name
      }
    }
  }
`

type TSchemaEnumValues = {
  __type: {
    name: string
    enumValues: Array<{ name: string }> | null
  } | null
}

export const useSchemaEnumValueProbe = (
  enumName: string,
  valueName: string,
  skip = false
): TSchemaFieldProbe => {
  const { data, loading } = useQuery<TSchemaEnumValues>(SCHEMA_ENUM_VALUES, {
    variables: { typeName: enumName },
    skip,
    fetchPolicy: 'cache-first',
  })
  if (skip || !data) return { supported: undefined, loading: !skip && loading }
  return {
    supported: Boolean(
      // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
      data.__type?.enumValues?.some((value) => value.name === valueName)
    ),
    loading: false,
  }
}

export const useSchemaEnumValueSupport = (
  enumName: string,
  valueName: string,
  skip = false
): boolean | undefined =>
  useSchemaEnumValueProbe(enumName, valueName, skip).supported

/**
 * QUERY-ARGUMENT sibling of `useSchemaFieldSupport` (COMPASS-1190).
 *
 * A document that passes an argument the connected backend's root field
 * does not (yet) declare — `allProperties(zoneIds: …)` against a backend
 * that predates the zone filter — fails GraphQL validation SERVER-SIDE and
 * takes the whole list query down, whatever the variable's value. The
 * `__type` probes above cannot see arguments (a field's args live under
 * `fields { args }`), so this one introspects the root Query type's field
 * arguments and lets a list keep a with-argument and a without-argument
 * document, selecting the former only once the live schema confirms it.
 *
 * One probe per session, shared by every caller: the root type is large,
 * so this is pinned `cache-first` (the app-wide default is
 * cache-and-network, which would re-fetch it on every mount). `undefined`
 * while in flight or on failure — treat as "not supported" (hide the
 * filter, send the plain document).
 */
export const SCHEMA_QUERY_FIELD_ARGS = gql`
  query SchemaQueryFieldArgs {
    __schema {
      queryType {
        fields {
          name
          args {
            name
          }
        }
      }
    }
  }
`

type TSchemaQueryFieldArgs = {
  __schema: {
    queryType: {
      fields: Array<{ name: string; args: Array<{ name: string }> }> | null
    } | null
  } | null
}

/**
 * `useSchemaQueryArgsSupport` with the probe's IN-FLIGHT state beside its
 * answer, as `useSchemaFieldsProbe` has (PRODUCT-416). A list that picks its
 * DOCUMENT by the answer — the pickup-order tables' `…WithLifecycle` twin —
 * must not send the plain document while the probe is still out: the answer
 * arriving would swap the document, Apollo starts a fresh query for a new
 * document, and the list would fetch twice and, with a saved lifecycle
 * filter, show a differently filtered page in between. `loading` lets such a
 * list hold its query until the probe settles — and ONLY while the probe is
 * outstanding: one that fails resolves to `loading: false` with `supported:
 * undefined`, so the list falls back to the plain document instead of
 * waiting forever.
 */
export const useSchemaQueryArgsProbe = (
  fieldName: string,
  argNames: readonly string[],
  skip = false
): TSchemaFieldProbe => {
  const { data, loading } = useQuery<TSchemaQueryFieldArgs>(
    SCHEMA_QUERY_FIELD_ARGS,
    { skip, fetchPolicy: 'cache-first' }
  )
  if (skip || !data) return { supported: undefined, loading: !skip && loading }
  // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
  const field = data.__schema?.queryType?.fields?.find(
    (candidate) => candidate.name === fieldName
  )
  const declared = new Set((field?.args ?? []).map((arg) => arg.name))
  return {
    supported: argNames.every((name) => declared.has(name)),
    loading: false,
  }
}

/**
 * ALL-OF form (COMPASS-1190 Map Planning): a second document that adds
 * SEVERAL arguments over its base — the map twins carry the zone filter AND
 * the "No area" null flag — may only be sent once the root field declares
 * EVERY one of them. One undeclared argument fails validation for the whole
 * operation exactly as one unknown field does, so a backend serving `zoneIds`
 * without `area_Isnull` must still get the base document. `true` only when
 * every name in `argNames` is declared; `undefined` while in flight/skipped.
 */
export const useSchemaQueryArgsSupport = (
  fieldName: string,
  argNames: readonly string[],
  skip = false
): boolean | undefined =>
  useSchemaQueryArgsProbe(fieldName, argNames, skip).supported

/** The one-argument form of `useSchemaQueryArgsProbe`. */
export const useSchemaQueryArgProbe = (
  fieldName: string,
  argName: string,
  skip = false
): TSchemaFieldProbe => useSchemaQueryArgsProbe(fieldName, [argName], skip)

/** The one-argument form: does the root field declare `argName`? */
export const useSchemaQueryArgSupport = (
  fieldName: string,
  argName: string,
  skip = false
): boolean | undefined => useSchemaQueryArgsSupport(fieldName, [argName], skip)

/**
 * ROOT-QUERY-FIELD sibling of `useSchemaMutationSupport` (PRODUCT-492; moved
 * here from the skip-hire-setup module in PRODUCT-503 so the generic probe
 * does not live in one feature's folder).
 *
 * `useSchemaQueryArgsSupport(field, [])` cannot answer "does the root field
 * exist" — an empty argument list is vacuously declared — so this reads the
 * same cached root-type probe and asks for the field by name.
 *
 * The probe form also carries the in-flight state (PRO-946), like
 * `useSchemaQueryArgsProbe`: a tab gated on a root field must not read "not
 * answered yet" as "no", or a deep link to it is redirected away before the
 * probe lands. `loading` is `false` once it resolved, failed or was skipped,
 * so a failing probe never parks the caller.
 */
export const useSchemaQueryFieldProbe = (
  fieldName: string,
  skip = false
): TSchemaFieldProbe => {
  const { data, loading } = useQuery<TSchemaQueryFieldArgs>(
    SCHEMA_QUERY_FIELD_ARGS,
    { skip, fetchPolicy: 'cache-first' }
  )
  if (skip || !data) return { supported: undefined, loading: !skip && loading }
  return {
    supported: Boolean(
      // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
      data.__schema?.queryType?.fields?.some(
        (candidate) => candidate.name === fieldName
      )
    ),
    loading: false,
  }
}

/**
 * The answer-only form of `useSchemaQueryFieldProbe`: `undefined` while in
 * flight or on failure — treat as "not supported".
 */
export const useSchemaQueryFieldSupport = (
  fieldName: string,
  skip = false
): boolean | undefined => useSchemaQueryFieldProbe(fieldName, skip).supported

/**
 * MUTATION-ARGUMENT sibling of `useSchemaQueryArgSupport` (HSY-235,
 * COMPASS-1687).
 *
 * The query-root probe above introspects `__schema.queryType` only, so a
 * document that passes an argument to a ROOT MUTATION the connected backend
 * does not (yet) declare — `bulkUpdateHaulerPrices(unitChangeValue: …)`
 * against a backend without the four-decimal contractor prices — would still
 * fail GraphQL validation server-side and take the whole mutation down. This
 * probes `__schema.mutationType` the same way and lets a form keep a
 * with-argument and a without-argument mutation document, selecting the
 * former only once the live schema confirms the argument.
 *
 * Same plumbing: one `cache-first` probe per session shared by every caller
 * (the root type is large); `undefined` while in flight or on failure —
 * treat as "not supported" (send the plain document).
 */
export const SCHEMA_MUTATION_FIELD_ARGS = gql`
  query SchemaMutationFieldArgs {
    __schema {
      mutationType {
        fields {
          name
          args {
            name
          }
        }
      }
    }
  }
`

type TSchemaMutationFieldArgs = {
  __schema: {
    mutationType: {
      fields: Array<{ name: string; args: Array<{ name: string }> }> | null
    } | null
  } | null
}

export const useSchemaMutationArgSupport = (
  fieldName: string,
  argName: string,
  skip = false
): boolean | undefined => {
  const { data } = useQuery<TSchemaMutationFieldArgs>(
    SCHEMA_MUTATION_FIELD_ARGS,
    { skip, fetchPolicy: 'cache-first' }
  )
  if (skip || !data) return undefined
  // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
  const field = data.__schema?.mutationType?.fields?.find(
    (candidate) => candidate.name === fieldName
  )
  return Boolean(field?.args?.some((arg) => arg.name === argName))
}

/**
 * MUTATION-EXISTENCE sibling of `useSchemaMutationArgSupport` (PRODUCT-453).
 *
 * A mutation document whose ROOT FIELD the connected backend does not (yet)
 * declare — `extendRental(…)` against a backend without the skip-hire
 * rentals lane — fails GraphQL validation server-side the moment it is
 * sent, so the action that fires it must not be offered until the live
 * schema lists the field. Same probe document and cache as the argument
 * form (one `cache-first` request per session shared by every caller);
 * `undefined` while in flight or on failure — treat as "not supported"
 * (hide the action).
 */
export const useSchemaMutationSupport = (
  fieldName: string,
  skip = false
): boolean | undefined => {
  const { data } = useQuery<TSchemaMutationFieldArgs>(
    SCHEMA_MUTATION_FIELD_ARGS,
    { skip, fetchPolicy: 'cache-first' }
  )
  if (skip || !data) return undefined
  return Boolean(
    // eslint-disable-next-line no-underscore-dangle -- GraphQL introspection meta-field
    data.__schema?.mutationType?.fields?.some(
      (candidate) => candidate.name === fieldName
    )
  )
}
